import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'

interface AuditHistory {
  id: number
  createdAt: string
  avgPerfMobile: number | null
  avgPerfDesktop: number | null
  avgSeo: number | null
  avgBp: number | null
}

interface ScoresChartProps {
  history: AuditHistory[]
}

const SERIES_CONFIG = [
  { key: 'avgPerfMobile' as const, label: 'Perf. Mobile',      color: '#6366F1' },
  { key: 'avgPerfDesktop' as const, label: 'Perf. Desktop',    color: '#3B82F6' },
  { key: 'avgSeo' as const,         label: 'SEO',              color: '#10B981' },
  { key: 'avgBp' as const,          label: 'Meilleures pratiques', color: '#F59E0B' },
]

function formatAxisDate(isoStr: string): string {
  const d = new Date(isoStr)
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

export default function ScoresChart({ history }: ScoresChartProps) {
  if (history.length < 2) return null

  const option = useMemo(() => {
    const dates = history.map(h => formatAxisDate(h.createdAt))

    const series = SERIES_CONFIG.map(cfg => ({
      name: cfg.label,
      type: 'line' as const,
      data: history.map(h => h[cfg.key]),
      smooth: 0.3,
      symbol: 'circle',
      symbolSize: 4,
      lineStyle: { color: cfg.color, width: 2 },
      itemStyle: { color: cfg.color, borderColor: '#fff', borderWidth: 1.5 },
      connectNulls: true,
    }))

    return {
      backgroundColor: 'transparent',
      legend: {
        top: 0,
        left: 0,
        itemWidth: 12,
        itemHeight: 12,
        icon: 'circle',
        textStyle: { color: '#8B8FA7', fontSize: 11 },
        inactiveColor: '#D1D5DB',
      },
      grid: {
        left: 40,
        right: 16,
        top: 36,
        bottom: 28,
      },
      xAxis: {
        type: 'category' as const,
        data: dates,
        axisLine: { lineStyle: { color: '#E2E4ED' } },
        axisLabel: { color: '#8B8FA7', fontSize: 10 },
        axisTick: { show: false },
        boundaryGap: false,
      },
      yAxis: {
        type: 'value' as const,
        min: 0,
        max: 100,
        axisLine: { show: false },
        axisLabel: { color: '#8B8FA7', fontSize: 10 },
        splitLine: {
          lineStyle: { color: '#F4F5FA', type: 'dashed' as const },
        },
        axisTick: { show: false },
      },
      series,
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: '#1B2559',
        borderColor: '#1B2559',
        borderRadius: 8,
        padding: [8, 12],
        textStyle: { color: '#fff', fontSize: 12 },
        formatter: (params: any[]) => {
          const date = params[0]?.name ?? ''
          const lines = params
            .filter(p => p.value !== null && p.value !== undefined)
            .map(
              p =>
                `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:5px;"></span>` +
                `${p.seriesName} : <b style="color:${p.color}">${p.value}</b>`
            )
            .join('<br/>')
          return `<span style="color:#8B8FA7;font-size:11px">${date}</span><br/>${lines}`
        },
      },
    }
  }, [history])

  return (
    <ReactECharts
      option={option}
      style={{ height: 300, width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  )
}
